import React, { useMemo } from "react";
import { motion } from "framer-motion";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

import { sowLabel, resolveStatusBadge } from "./engagementUtils";

/* --------------------------- Sub components --------------------------- */

const StatCard = ({ eyebrow, value, subtitle, accent }) => (
  <div
    style={{
      flex: 1,
      minWidth: 180,
      background: "var(--color-bg-card)",
      border: "1px solid var(--color-border)",
      borderRadius: 12,
      padding: "16px 18px 14px",
      position: "relative",
      boxShadow: "var(--shadow-sm)",
      overflow: "hidden",
    }}
  >
    <span
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        background: accent,
      }}
    />
    <div
      style={{
        fontSize: 10.5,
        fontWeight: 700,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        color: "var(--color-text-light)",
        marginBottom: 8,
      }}
    >
      {eyebrow}
    </div>
    <div
      style={{
        fontSize: 22,
        fontWeight: 700,
        color: "var(--color-text)",
        lineHeight: 1.15,
        marginBottom: 6,
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
    {subtitle && (
      <div
        style={{
          fontSize: 12,
          color: "var(--color-text-light)",
          display: "flex",
          alignItems: "center",
          gap: 6,
        }}
      >
        {subtitle.dot && (
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: subtitle.dot,
              display: "inline-block",
            }}
          />
        )}
        <span>{subtitle.text}</span>
      </div>
    )}
  </div>
);

const Panel = ({ title, subtitle, children, style }) => (
  <div
    style={{
      background: "var(--color-bg-card)",
      border: "1px solid var(--color-border)",
      borderRadius: 12,
      padding: 18,
      boxShadow: "var(--shadow-sm)",
      ...style,
    }}
  >
    <div style={{ marginBottom: 12 }}>
      <h6
        style={{
          fontSize: 14,
          fontWeight: 700,
          color: "var(--color-text)",
          margin: 0,
        }}
      >
        {title}
      </h6>
      {subtitle && (
        <div style={{ fontSize: 12, color: "var(--color-text-light)", marginTop: 2 }}>
          {subtitle}
        </div>
      )}
    </div>
    {children}
  </div>
);

/* --------------------------- Helpers --------------------------- */

const flattenItems = (deal) =>
  (deal.categories || []).flatMap((c) => (c.items || []).map((it) => ({ ...it, category: c.category })));

const findField = (deal, patterns) => {
  const items = flattenItems(deal);
  for (const pat of patterns) {
    const re = new RegExp(pat, "i");
    const m = items.find((it) => re.test(it.name || ""));
    if (m && m.value) return String(m.value);
  }
  return null;
};

const formatTimelineDate = (raw) => {
  if (!raw) return null;
  const d = new Date(raw);
  if (isNaN(d.getTime())) return raw;
  return d.toLocaleDateString(undefined, { month: "short", year: "numeric" });
};

/* --------------------------- Main --------------------------- */

const AnalyticsTab = ({ engagementDeals = [], resellDeals = [] }) => {
  const totalEngagements = engagementDeals.length;
  const totalResell = resellDeals.length;

  /* ---------- Top stat cards ---------- */
  const allActiveEngagement =
    totalEngagements > 0 &&
    engagementDeals.every((d) => resolveStatusBadge(d).label === "Active");

  const completedResell = useMemo(
    () => resellDeals.filter((d) => resolveStatusBadge(d).label === "Complete").length,
    [resellDeals]
  );

  const dealStatus = useMemo(() => {
    if (!totalResell && !totalEngagements) return { label: "—", subtitle: "No deals" };
    if (totalResell && completedResell === totalResell)
      return { label: "Complete", subtitle: `${completedResell} Resell delivered` };
    if (allActiveEngagement) return { label: "Active", subtitle: "All engagements active" };
    return { label: "In Progress", subtitle: "Some deals in progress" };
  }, [completedResell, totalResell, totalEngagements, allActiveEngagement]);

  /* ---------- Bar chart data ---------- */
  const barData = [
    { name: "Engagements", value: totalEngagements },
    { name: "Resell Deals", value: totalResell },
  ];

  /* ---------- Donut chart data ---------- */
  const allDeals = useMemo(
    () => [...engagementDeals, ...resellDeals],
    [engagementDeals, resellDeals]
  );

  const donutCounts = useMemo(() => {
    let completed = 0;
    let inProgress = 0;
    let pending = 0;
    allDeals.forEach((d) => {
      const label = resolveStatusBadge(d).label.toLowerCase();
      if (label.includes("complete")) completed += 1;
      else if (label.includes("active") || label.includes("in progress")) inProgress += 1;
      else pending += 1;
    });
    return { completed, inProgress, pending };
  }, [allDeals]);

  const donutData = [
    { name: "Completed", value: donutCounts.completed, color: "#22c55e" },
    { name: "In Progress", value: donutCounts.inProgress, color: "#3b82f6" },
    { name: "Pending", value: donutCounts.pending, color: "#f59e0b" },
  ];
  const donutTotal = donutCounts.completed + donutCounts.inProgress + donutCounts.pending;

  /* ---------- Resell deal details summary (first resell deal) ---------- */
  const featuredResell = resellDeals[0] || null;
  const resellDetails = useMemo(() => {
    if (!featuredResell) return null;
    return [
      { label: "Deal", value: featuredResell.deal_name },
      {
        label: "Vendor",
        value: findField(featuredResell, ["vendor", "supplier", "manufacturer"]),
      },
      {
        label: "Customer",
        value: findField(featuredResell, ["customer", "client legal name", "client name"]) ||
          featuredResell.client_name,
      },
      {
        label: "Pricing",
        value: findField(featuredResell, ["pricing model", "fee type", "deal type"]),
      },
      {
        label: "Payment Terms",
        value: findField(featuredResell, ["payment terms", "net[\\s-]?\\d+", "terms"]),
      },
      {
        label: "Status",
        value: resolveStatusBadge(featuredResell).label,
      },
    ];
  }, [featuredResell]);

  /* ---------- Timeline & Activity ---------- */
  const timelineEntries = useMemo(() => {
    const items = [];
    resellDeals.forEach((d, i) => {
      const status = resolveStatusBadge(d);
      const vendor = findField(d, ["vendor", "supplier"]);
      items.push({
        date: d.close_date ? formatTimelineDate(d.close_date) : null,
        title: `${sowLabel(d, i)} ${status.label === "Complete" ? "Project Complete" : status.label}`,
        subtitle: vendor
          ? `${vendor} — ${d.deal_name}`
          : d.deal_name,
        color: status.color,
        active: status.label === "Complete",
      });
    });
    engagementDeals.forEach((d, i) => {
      const status = resolveStatusBadge(d);
      const duration = findField(d, ["project duration", "duration"]);
      items.push({
        date: null,
        title: `${sowLabel(d, i)} ${d.deal_name}`,
        subtitle: `${status.label}${duration ? ` · ${duration}` : ""}`,
        color: status.color,
        active: status.label === "Active",
      });
    });
    items.push({
      date: null,
      title: "Next Renewal Due",
      subtitle: "No upcoming renewals",
      color: "#9ca3af",
      active: false,
    });
    return items;
  }, [engagementDeals, resellDeals]);

  /* ---------------------------------------------------------------- */

  const containerAnim = {
    initial: { opacity: 0, y: 12 },
    animate: { opacity: 1, y: 0 },
  };

  return (
    <motion.div {...containerAnim} transition={{ duration: 0.25 }}>
      {/* Top stat cards */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
          gap: 14,
          marginBottom: 18,
        }}
      >
        <StatCard
          eyebrow="Total Engagements"
          value={totalEngagements}
          accent="#3b82f6"
          subtitle={{
            text:
              totalEngagements === 0
                ? "No engagements"
                : allActiveEngagement
                ? "Both active"
                : `${totalEngagements} total`,
          }}
        />
        <StatCard
          eyebrow="Total Resell Deals"
          value={totalResell}
          accent="#a855f7"
          subtitle={{
            text:
              totalResell === 0
                ? "No deals"
                : completedResell === totalResell
                ? `${completedResell} Completed`
                : `${totalResell} total`,
          }}
        />
        <StatCard
          eyebrow="Deal Status"
          value={dealStatus.label}
          accent="#22c55e"
          subtitle={{
            dot: dealStatus.label === "Complete" ? "#22c55e" : "#3b82f6",
            text: dealStatus.subtitle,
          }}
        />
      </div>

      {/* Charts row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <Panel title="Engagement vs Resell Overview" subtitle="Active engagements and resell deals">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart
              layout="vertical"
              data={barData}
              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
            >
              <CartesianGrid stroke="var(--color-border)" horizontal={false} />
              <XAxis
                type="number"
                allowDecimals={false}
                tick={{ fontSize: 11, fill: "var(--color-text-light)" }}
              />
              <YAxis
                type="category"
                dataKey="name"
                tick={{ fontSize: 12, fill: "var(--color-text-light)" }}
                width={90}
              />
              <RechartsTooltip
                cursor={{ fill: "rgba(114,105,239,0.08)" }}
                contentStyle={{
                  background: "var(--color-bg-card)",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <Bar dataKey="value" fill="#7269ef" radius={[0, 6, 6, 0]} barSize={22} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>

        <Panel title="Deal Status Breakdown">
          <div style={{ display: "flex", gap: 16, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ width: 200, height: 200, position: "relative" }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey="value"
                    innerRadius={60}
                    outerRadius={90}
                    paddingAngle={3}
                    stroke="none"
                  >
                    {donutData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <RechartsTooltip
                    contentStyle={{
                      background: "var(--color-bg-card)",
                      border: "1px solid var(--color-border)",
                      borderRadius: 8,
                      fontSize: 12,
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  pointerEvents: "none",
                }}
              >
                <span
                  style={{
                    fontSize: 22,
                    fontWeight: 700,
                    color: "var(--color-text)",
                  }}
                >
                  {donutTotal}
                </span>
                <span style={{ fontSize: 11, color: "var(--color-text-light)" }}>
                  Total Deals
                </span>
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, minWidth: 140 }}>
              {donutData.map((d) => (
                <div
                  key={d.name}
                  style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 12.5 }}
                >
                  <span
                    style={{
                      width: 10,
                      height: 10,
                      borderRadius: "50%",
                      background: d.color,
                      display: "inline-block",
                    }}
                  />
                  <span style={{ color: "var(--color-text)", flex: 1 }}>{d.name}</span>
                  <span style={{ fontWeight: 700, color: "var(--color-text)" }}>{d.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Panel>
      </div>

      {/* Bottom row */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
          gap: 16,
          alignItems: "start",
        }}
      >
        <Panel
          title="Resell Deal Details Summary"
          subtitle={featuredResell ? `${sowLabel(featuredResell)} breakdown` : "No resell deals"}
        >
          {!resellDetails && (
            <div style={{ fontSize: 13, color: "var(--color-text-light)" }}>
              No resell deal details to display.
            </div>
          )}
          {resellDetails && (
            <div style={{ display: "flex", flexDirection: "column" }}>
              {resellDetails.map((row, idx) => (
                <div
                  key={idx}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                    padding: "10px 0",
                    borderBottom:
                      idx === resellDetails.length - 1 ? "none" : "1px solid var(--color-border)",
                  }}
                >
                  <span style={{ fontSize: 12, color: "var(--color-text-light)", fontWeight: 500 }}>
                    {row.label}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      color: "var(--color-text)",
                      fontWeight: 600,
                      textAlign: "right",
                      wordBreak: "break-word",
                      maxWidth: "60%",
                    }}
                  >
                    {row.value || "—"}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Panel>

        <Panel title="Timeline & Activity" subtitle="Engagement and resell milestones">
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {timelineEntries.map((entry, idx) => (
              <div
                key={idx}
                style={{
                  display: "flex",
                  gap: 12,
                  paddingBottom: 12,
                  position: "relative",
                }}
              >
                <div
                  style={{
                    width: 14,
                    height: 14,
                    borderRadius: "50%",
                    background: entry.active ? entry.color : "transparent",
                    border: `2px solid ${entry.color}`,
                    flexShrink: 0,
                    marginTop: 2,
                    position: "relative",
                    zIndex: 1,
                  }}
                />
                {idx !== timelineEntries.length - 1 && (
                  <div
                    style={{
                      position: "absolute",
                      left: 6,
                      top: 18,
                      bottom: 0,
                      width: 2,
                      background: "var(--color-border)",
                    }}
                  />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span
                      style={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: "var(--color-text)",
                      }}
                    >
                      {entry.title}
                    </span>
                    {entry.date && (
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 600,
                          color: "var(--color-text-light)",
                          background: "var(--color-bg)",
                          padding: "1px 6px",
                          borderRadius: 4,
                          border: "1px solid var(--color-border)",
                        }}
                      >
                        {entry.date}
                      </span>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 12,
                      color: "var(--color-text-light)",
                      marginTop: 2,
                      wordBreak: "break-word",
                    }}
                  >
                    {entry.subtitle}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Panel>
      </div>
    </motion.div>
  );
};

export default AnalyticsTab;
